import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select
} from '@mui/material'
import { FC, useCallback, useState } from 'react'

import { getPromptAnswer } from '../../../components/Inspections/getPromptAnswer'
import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  EntryWithRelated,
  NotInspectedDummyEntry,
  PromptWithRelated
} from '../../../models/inspections'
import {
  useUpdateInspectionEntryMutation,
  useUpdateInspectionPromptMutation
} from '../../../redux/api/inspectionApi'
import { InspectionEntryStatusEntry } from '../../../sdk'

interface MultipleChoiceMultipleSelectProps {
  prompt: PromptWithRelated
  entry: EntryWithRelated
  setEntry: (entry: EntryWithRelated) => void
  allStatuses: InspectionEntryStatusEntry[] | undefined
  handleSelectAnswer: (updatedEntry: EntryWithRelated) => void
}
export const PromptSelectForMultiSelect: FC<
  MultipleChoiceMultipleSelectProps
> = ({ prompt, entry, setEntry, allStatuses, handleSelectAnswer }) => {
  const { accessToken } = useAccessTokenAndShopId()
  const [updatePrompt] = useUpdateInspectionPromptMutation()
  const [updateEntry] = useUpdateInspectionEntryMutation()
  const [open, setOpen] = useState(false)

  const selected = getPromptAnswer(prompt.info!) as string[]
  const onSubmitForMultipleChoiceMultipleSelect = useCallback(
    async (value: string[]) => {
      const updatedPrompt: PromptWithRelated = {
        ...prompt,
        info: {
          ...prompt.info!,
          answer: undefined,
          multipleChoiceAnswer: undefined,
          multipleChoiceMultipleSelectAnswers:
            value.length === 0 ? undefined : value
        }
      }

      const answers = prompt.answers.filter((answer) =>
        value.includes(answer.id!)
      )
      const customerNotesArr: string[] = []
      let customStatus = entry.info!.customStatus
      answers.forEach((answer) => {
        if (answer.info!.customerNotes) {
          customerNotesArr.push(answer.info!.customerNotes)
        }
        // TODO: define the logic for customStatus if there are multiple answers with different statuses
        if (answer.info!.status) {
          customStatus = answer.info!.status
        }
      })
      const updatedEntry: EntryWithRelated = {
        ...entry,
        info: {
          ...entry.info!,
          customStatus,
          customerNotes: customerNotesArr.join('\n')
        },
        status:
          allStatuses?.find((s) => s.id === customStatus) ||
          NotInspectedDummyEntry,
        prompts: [updatedPrompt]
      }
      // update UI before sending request
      setEntry(updatedEntry)
      updatePrompt({
        authorization: accessToken,
        promptEntry: updatedPrompt
      })
      updateEntry({
        authorization: accessToken,
        entryEntry: updatedEntry
      })
    },
    [
      accessToken,
      allStatuses,
      entry,
      prompt,
      setEntry,
      updateEntry,
      updatePrompt
    ]
  )

  const handleCloseSelect = useCallback(() => {
    setOpen(false)
    handleSelectAnswer(entry)
  }, [entry, handleSelectAnswer])

  return (
    <FormControl variant="standard" required={prompt.info?.required}>
      <InputLabel id="multi-answer-label">Answer</InputLabel>
      <Select
        labelId="multi-answer-label"
        id="multi-answer"
        multiple
        value={selected}
        onChange={(event) => {
          if (
            event.target.value.length === 1 &&
            event.target.value[0] === 'close'
          ) {
            return
          }
          onSubmitForMultipleChoiceMultipleSelect(
            (event.target.value as string[]).filter(
              (value) => value !== 'close'
            )
          )
        }}
        renderValue={() =>
          prompt.answers
            .filter((answer) => selected.includes(answer.id!))
            .map((answer) => answer.info?.question)
            .join(', ')
        }
        required={prompt.info?.required}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleCloseSelect}>
        {prompt.answers.map((answer) => (
          <MenuItem key={answer.id} value={answer.id}>
            <Checkbox checked={selected.indexOf(answer.id!) > -1} />
            <ListItemText primary={answer.info?.question} />
          </MenuItem>
        ))}
        <MenuItem key={'close'} value={'close'} onClick={handleCloseSelect}>
          Close
        </MenuItem>
      </Select>
    </FormControl>
  )
}
